import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Link, linkOverlayBoundaryClassName } from '@change/ds-core-ui/Link';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';

import type { MediaLinkPreview } from '../../../../api/mediaLinkPreview';
import type { MediaHitEmbed } from '../../../../shared/context';
import { useMediaHitsPermissions } from '../../../../shared/hooks/useMediaHitsPermissions';
import { DeleteMediaHit } from '../../../DeleteMediaHit';
import { LoadingSkeleton } from '../../../LoadingSkeleton';
import { InvalidMediaHitContent } from '../../../MediaHitList/components/MediaHit';
import { MediaHitByline, MediaHitCta, MediaHitThumbnail } from '../../MediaHitParts';

type Props = Readonly<{
	media: MediaHitEmbed;
	removeMedia: (mediaId: string) => void;
	featured?: boolean;
}>;

const MediaHitItemFields: React.FC<{ card: MediaLinkPreview; featured: boolean }> = ({ card, featured }) => (
	<>
		<MediaHitThumbnail card={card} featured={featured} />
		<MediaHitByline card={card} />
		{!!card.title && (
			<Text
				as="h3"
				variant="heading"
				size="xx-small"
				expressive
				fontWeight="bold"
				className={cn('line-clamp-3', featured ? 'md:text-size-heading-small' : 'md:line-clamp-2')}
			>
				{card.title}
			</Text>
		)}
		{!!card.description && (
			// Below `md` every item is a single stacked column with room to spare, so all of them show the
			// description there; at `md`+ the two-column grid only has room for the featured item's.
			<Text as="p" size="small" className={cn('text-typography-strong line-clamp-3', !featured && 'md:hidden')}>
				{card.description}
			</Text>
		)}
		<MediaHitCta card={card} />
	</>
);

const ValidMediaHitItem: React.FC<Required<Pick<Props, 'media' | 'featured'>>> = ({ media, featured }) => {
	const { translate } = useI18n();
	const track = useTracking();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();

	const { card } = media;
	const outlet = card?.outletName;

	const handleClick = (): void => {
		void track('petition_media_feed_click', {
			url: media.url,
			petition_id: petitionId,
			media_source: media.mediaSource,
		});
	};

	// outletName is nullable in the schema — falling through to `%{outlet}` unguarded would leave a
	// dangling "— " or "on " in the accessible name once the placeholder resolves to an empty string.
	const linkLabel = (() => {
		if (card?.title) {
			return outlet
				? translate('corgi.petition_gamma.media_mentions.card.article_link_label', { title: card.title, outlet })
				: card.title;
		}
		return outlet
			? translate('corgi.petition_gamma.media_mentions.card.untitled_article_link_label', { outlet })
			: translate('corgi.petition_gamma.media_mentions.card.untitled_article_link_label_no_outlet');
	})();

	return (
		<>
			{!!card && <MediaHitItemFields card={card} featured={featured} />}
			<Link
				overlay
				href={media.url}
				target="_blank"
				aria-label={linkLabel}
				onClick={handleClick}
				data-qa="media-hit-item-link"
			/>
		</>
	);
};

export const MediaHitItem: React.FC<Props> = ({ media, removeMedia, featured = false }) => {
	const {
		data: { canDelete },
	} = useMediaHitsPermissions();

	if (media.status === 'invalid' && !canDelete) return null;

	return (
		<div
			data-testid={`media-hit-item-${media.id}`}
			className={cn('relative flex flex-col gap-3', linkOverlayBoundaryClassName)}
		>
			{media.status === 'loading' && <LoadingSkeleton />}
			{media.status === 'valid' && <ValidMediaHitItem media={media} featured={featured} />}
			{media.status === 'invalid' && <InvalidMediaHitContent media={media} removeMedia={removeMedia} />}
			{media.status === 'valid' && canDelete && (
				<div className="mt-1">
					<DeleteMediaHit media={media} removeMedia={removeMedia} />
				</div>
			)}
		</div>
	);
};
